import React, { useState, useRef } from 'react';
import { Camera, CheckCircle, X, Plus } from '@phosphor-icons/react';
import { bookingApi, formatApiError } from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { resolveStorageUrl } from '../services/storage';

/**
 * Strukturiertes Foto-Slot-Grid für Übergabe (check-in) und Rückgabe (check-out).
 *
 * Props:
 *  - bookingId: string
 *  - phase: 'checkin' | 'checkout'
 *  - slots: [{ key, label, required, active }] — effektive Slot-Konfiguration aus den Settings
 *  - existingPhotos: aktueller Inhalt von booking.check_in.photos / booking.check_out.photos
 *      (Array aus Strings (legacy) oder {slot, path, label} Objekten)
 *  - onChange: (newPhotos) => void  — wird nach erfolgreichem Upload/Delete aufgerufen
 *
 * Verhalten: Pflicht-Slots zeigen einen roten Indikator solange leer. Komponente berichtet
 * den Aufnahme-Stand über `getCheckinReadiness()` (siehe Helper unten).
 */
const PhotoSlotGrid = ({ bookingId, phase, slots, existingPhotos, onChange }) => {
  const [uploading, setUploading] = useState(null); // slot key
  const fileInputs = useRef({});
  const activeSlots = (slots || []).filter((s) => s.active !== false);

  // Index existing photos by slot
  const photosBySlot = {};
  const legacyPhotos = [];
  const extraPhotos = [];
  (existingPhotos || []).forEach((p) => {
    if (typeof p === 'string') {
      legacyPhotos.push(p);
    } else if (p && p.slot) {
      if (p.slot === 'extra') {
        extraPhotos.push(p);
      } else {
        photosBySlot[p.slot] = p;
      }
    }
  });

  const handlePick = (slotKey) => {
    fileInputs.current[slotKey]?.click();
  };

  const handleFileChange = async (slotKey, e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-pick of same file works
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Foto zu groß (max. 15 MB)');
      return;
    }
    try {
      setUploading(slotKey);
      const res = await bookingApi.uploadPhoto(bookingId, phase, file, slotKey);
      onChange?.(res.data.photos || []);
      toast.success('Foto aufgenommen');
    } catch (err) {
      toast.error(formatApiError(err) || 'Foto-Upload fehlgeschlagen');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (slot, path) => {
    if (!window.confirm('Foto wirklich entfernen?')) return;
    try {
      const res = await bookingApi.deletePhoto(bookingId, phase, { slot, path });
      onChange?.(res.data.photos || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'Foto konnte nicht entfernt werden');
    }
  };

  const requiredCount = activeSlots.filter((s) => s.required).length;
  const requiredDone = activeSlots.filter((s) => s.required && photosBySlot[s.key]).length;
  const allRequiredDone = requiredDone >= requiredCount;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="overline">Foto-Protokoll</p>
        <span
          className={`text-xs font-semibold ${allRequiredDone ? 'text-[#009951]' : 'text-[#E60000]'}`}
          data-testid={`${phase}-photo-progress`}
        >
          {requiredDone} / {requiredCount} Pflicht-Fotos
        </span>
      </div>

      {!allRequiredDone && (
        <div className="text-xs text-[#E60000] bg-[#FFF1F1] border border-[#E60000]/20 rounded-sm px-3 py-2">
          Achtung: {requiredCount - requiredDone} Pflicht-Foto(s) fehlen noch. Der Vorgang kann trotzdem abgeschlossen werden.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
        {activeSlots.map((slot) => {
          const photo = photosBySlot[slot.key];
          const isUploading = uploading === slot.key;
          const isExtra = slot.key === 'extra';
          // For 'extra' we render the input but allow multi-upload via a separate section below
          if (isExtra) return null;
          return (
            <div
              key={slot.key}
              className={`relative border rounded-sm overflow-hidden ${
                photo
                  ? 'border-[#009951]'
                  : slot.required
                    ? 'border-[#E60000]/40 bg-[#FFF8F8]'
                    : 'border-[#E2E4E9] bg-[#FAFAFB]'
              }`}
              data-testid={`${phase}-slot-${slot.key}`}
            >
              <input
                ref={(el) => (fileInputs.current[slot.key] = el)}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileChange(slot.key, e)}
                data-testid={`${phase}-slot-${slot.key}-input`}
              />
              <button
                type="button"
                onClick={() => handlePick(slot.key)}
                className="block w-full aspect-square focus:outline-none group"
              >
                {photo ? (
                  <img
                    src={resolveStorageUrl(photo.path)}
                    alt={slot.label}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-[#525866] group-hover:text-[#E60000] transition-colors">
                    <Camera weight={isUploading ? 'fill' : 'duotone'} size={28} className={isUploading ? 'animate-pulse' : ''} />
                    <span className="text-[10px] uppercase tracking-wide">
                      {isUploading ? 'Lade ...' : 'Aufnehmen'}
                    </span>
                  </div>
                )}
              </button>
              {photo && (
                <button
                  type="button"
                  onClick={() => handleDelete(slot.key)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 shadow-sm hover:bg-[#E60000] hover:text-white transition"
                  aria-label={`${slot.label} entfernen`}
                  data-testid={`${phase}-slot-${slot.key}-remove`}
                >
                  <X size={12} />
                </button>
              )}
              <div className="px-1.5 py-1 bg-white border-t border-[#E2E4E9]">
                <div className="flex items-center gap-1 text-[11px] leading-tight">
                  {photo && <CheckCircle weight="fill" size={11} className="text-[#009951] flex-shrink-0" />}
                  <span className="font-medium text-[#111418] truncate">{slot.label}</span>
                  {slot.required && !photo && <span className="text-[#E60000] ml-auto">*</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Zusatz-Fotos (multi-upload) */}
      {activeSlots.some((s) => s.key === 'extra') && (
        <div className="pt-3 border-t border-[#E2E4E9]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#111418]">Zusatzfotos</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handlePick('extra')}
              data-testid={`${phase}-slot-extra-pick-btn`}
            >
              <Plus size={14} className="mr-1" /> Foto hinzufügen
            </Button>
          </div>
          <input
            ref={(el) => (fileInputs.current['extra'] = el)}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFileChange('extra', e)}
            data-testid={`${phase}-slot-extra-input`}
          />
          {extraPhotos.length === 0 && legacyPhotos.length === 0 ? (
            <p className="text-xs text-[#8A94A6]">Keine Zusatzfotos.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
              {extraPhotos.map((p) => (
                <div key={p.path} className="relative aspect-square border border-[#E2E4E9] rounded-sm overflow-hidden">
                  <img src={resolveStorageUrl(p.path)} alt="Zusatzfoto" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleDelete(null, p.path)}
                    className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 shadow-sm hover:bg-[#E60000] hover:text-white"
                    aria-label="Entfernen"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
              {legacyPhotos.map((p) => (
                <div key={p} className="relative aspect-square border border-[#E2E4E9] rounded-sm overflow-hidden">
                  <img src={resolveStorageUrl(p)} alt="Altes Foto" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleDelete(null, p)}
                    className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 shadow-sm hover:bg-[#E60000] hover:text-white"
                    aria-label="Entfernen"
                    title="Altes Foto (vor Slot-System)"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoSlotGrid;
